/**
 * MindBridge - Conversation Agent (rebuilt)
 *
 * Design principles:
 * - Acts as an expert psychiatrist, not a reflective listening bot
 * - Emotionally intelligent: reads subtext, not just surface words
 * - Steers conversation toward goals without the patient noticing
 * - Never repeats sentence openers or question patterns
 * - Returns plain text to UI; JSON is internal only
 */

import { callGroq } from "./decomposer";

const normalize = (text) =>
  String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const jaccard = (a, b) => {
  const A = new Set(normalize(a).split(" ").filter(Boolean));
  const B = new Set(normalize(b).split(" ").filter(Boolean));
  if (!A.size || !B.size) return 0;
  let shared = 0;
  A.forEach((t) => {
    if (B.has(t)) shared += 1;
  });
  return shared / (A.size + B.size - shared);
};

const lastN = (messages, role, n) => messages.filter((m) => m.role === role).slice(-n);

const buildHistory = (messages, patientName) =>
  messages
    .slice(-14)
    .map((m) => `${m.role === "ai" ? "MindBridge" : patientName}: ${m.text}`)
    .join("\n");

const parseModelOutput = (raw) => {
  const cleaned = String(raw || "").replace(/```json|```/g, "").trim();
  const start = cleaned.indexOf("{");
  const end = cleaned.lastIndexOf("}");
  if (start === -1 || end <= start) return null;

  try {
    const obj = JSON.parse(cleaned.slice(start, end + 1));
    return {
      message: String(obj.message || "").trim(),
      goalComplete:
        obj.goalComplete === true || String(obj.goalComplete || "").toLowerCase() === "true",
      confidence: Math.min(1, Math.max(0, Number(obj.confidence) || 0)),
    };
  } catch (_error) {
    return null;
  }
};

const extractFallbackText = (raw) => {
  const cleaned = String(raw || "")
    .replace(/\{[\s\S]*\}/g, "")
    .replace(/```[\s\S]*?```/g, "")
    .replace(/["{}]/g, "")
    .trim();
  return cleaned || null;
};

const BANNED_OPENERS = [
  "it sounds like",
  "it seems like",
  "i hear you",
  "i understand",
  "that makes sense",
  "thank you for sharing",
];

const startsWithBannedOpener = (text) => {
  const t = normalize(text);
  return BANNED_OPENERS.some((o) => t.startsWith(o));
};

const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const wordCount = (text) => normalize(text).split(" ").filter(Boolean).length;

const starterKey = (text) => normalize(text).split(" ").filter(Boolean).slice(0, 4).join(" ");

const hasMultipleChoiceQuestion = (text) => {
  const low = String(text || "").toLowerCase();
  const hasQuestion = low.includes("?");
  if (!hasQuestion) return false;
  const orCount = (low.match(/\bor\b/g) || []).length;
  const commaList = /\b(is it|are you|do you feel)\b[^?]*,[^?]*\bor\b[^?]*\?/i.test(low);
  return orCount >= 2 || commaList;
};

const hasDanglingEnding = (text) => {
  const t = String(text || "").trim();
  if (!t) return true;

  // Most complete assistant replies should end with terminal punctuation.
  const endsWithTerminalPunctuation = /[.!?]["')\]]?$/.test(t);
  if (!endsWithTerminalPunctuation) return true;

  // Catch common cut-off tails like "when you" or "or if it".
  const tail = normalize(t).split(" ").slice(-4).join(" ");
  return /\b(and|or|but|if|when|because|while|that|which|who|where|to|of|for|with|in|on|at|from)\s+(i|you|we|they|it|he|she)?$/.test(tail);
};

const runConversationAgent = async ({
  currentGoal,
  remainingGoals = [],
  messages,
  memoryChunks = [],
  patientName,
  callModel,
}) => {
  const history = buildHistory(messages, patientName);
  const lastUserMsg = lastN(messages, "user", 1)[0]?.text || "";
  const lastAiMsgs = lastN(messages, "ai", 4).map((m) => String(m.text || ""));
  const isOpening = messages.filter((m) => m.role === "user").length === 0;
  const turnCount = messages.filter((m) => m.role === "user").length;
  const patientIsBrief = wordCount(lastUserMsg) > 0 && wordCount(lastUserMsg) <= 5;

  const memoryBlock = memoryChunks.length
    ? `\n\n--- Relevant history from past sessions ---\n${memoryChunks.join("\n")}\n---`
    : "";

  const steeringHint =
    remainingGoals.length > 0
      ? `After the current goal is covered, you will need to explore: ${remainingGoals
          .slice(0, 2)
          .join("; ")}. Keep this in mind when bridging topics naturally.`
      : "This is the final goal. Wrap up warmly once it is fully covered.";

  const recentOpeners = lastAiMsgs.length
    ? lastAiMsgs.map((m) => `"${m.slice(0, 40)}..."`).join("\n   ")
    : '"(none yet)"';

  const system = `You are MindBridge, an expert psychiatric interviewer with 20 years of clinical experience. You are conducting a structured mental health check-in on behalf of a licensed therapist. The patient does not know you have specific goals - the conversation must feel natural and supportive at all times.

CURRENT ASSESSMENT GOAL (keep to yourself): "${currentGoal}"
STEERING NOTE: ${steeringHint}
${memoryBlock}

=== YOUR CLINICAL APPROACH ===

1. EMOTIONAL INTELLIGENCE FIRST
   - Read between the lines. When a patient says "i hate everyone", they are expressing pain, not aggression.
   - Acknowledge the emotional weight of what was said SPECIFICALLY - reference their actual words.
   - Never give generic validation. If they said "always tired", your response mentions tiredness, not "feelings".

2. VARY YOUR SENTENCE OPENERS STRICTLY
   You have already used these recently - do NOT start your message with any of these patterns:
   ${recentOpeners}
   Use fresh, natural openings. Examples of varied openers you can rotate through:
   - Direct follow-up: "That constant exhaustion sounds..."
   - Gentle naming: "Weeks of feeling that way..."
   - Bridging: "When you mention [X], it makes me wonder..."
   - Quiet check-in: "How long has [specific thing] been this way?"
   - Reflective but specific: "Withdrawing from people when you're already drained..."

3. GOAL-DRIVEN BUT INVISIBLE
   - You have a specific clinical goal. The patient must never feel interrogated.
   - Use natural bridges: "You mentioned X - I'm curious whether that connects to how you've been sleeping?"
   - If the patient drifts, gently redirect within 1-2 turns.
   - If they give a short or unclear answer, probe specifically, not generically.

4. QUESTION DISCIPLINE
   - Ask ONE question per turn, maximum.
   - Make it specific to what they just said - not a generic "tell me more".
   - If you just asked a question last turn and they answered, reflect first, then decide if another question is needed.
   - Short patient messages ("yes", "idk", "no") need a warm specific prompt, not another open question.

5. PACING, VALIDATION, AND FLOW
  - Match the patient's energy. If they write briefly, your reply should be brief too.
  - Always validate before asking: first one sentence of genuine empathy, then one focused question.
  - Never give multiple-choice feelings prompts (avoid "is it A, B, or C?"). Ask open-ended questions.
  - If the patient seems withdrawn or answers very briefly, slow down and prioritize warmth over speed.
  - Occasionally reflect what you heard before asking more.
  - Never start two consecutive responses with the same sentence structure.

6. GOAL COMPLETION
   - Mark goalComplete = true only when you have gathered MEANINGFUL information about the goal.
   - Sufficient = patient has described: what, how long, how it affects them, and at least one specific detail.
   - Do NOT mark complete just because the patient gave one short answer.
   - confidence score: 0.0-1.0. Mark goalComplete only when confidence >= 0.75.

7. SAFETY
   - If the patient expresses suicidal thoughts, self-harm, or acute crisis: do NOT continue clinical goals.
   - Respond with warmth, acknowledge seriousness, and include: "I want to make sure you're safe. Please reach out to your therapist directly or contact a crisis line."
   - Set goalComplete = false and confidence = 0.

=== STYLE EXAMPLE ===
Prefer this style:
"Two months is a long time to be carrying that. Are you managing to get through your days okay, or is it hitting you pretty hard?"
Avoid repetitive templates like:
"It sounds like... Could you tell me more...?"

=== OUTPUT FORMAT ===
Return ONLY a valid JSON object. No markdown, no code fences, no text outside the JSON.

{
  "message": "Your full response to the patient. One natural paragraph. 2-4 sentences max. The reflection AND the question (if any) go here together - do not separate them.",
  "goalComplete": false,
  "confidence": 0.0
}

If goalComplete is true, write a warm natural closing sentence in message - do not say "goal complete" or anything clinical.`;

  const userPrompt = isOpening
    ? `This is the opening of the session. ${patientName} has not said anything yet. Write a warm, natural greeting that invites them to share how they have been. Do not mention goals or assessments. Keep it to 2 sentences.`
    : `Conversation so far:
${history}

Patient's latest message: "${lastUserMsg}"

Patient energy: ${patientIsBrief ? "brief/withdrawn" : "engaged/detailed"}

Turn number: ${turnCount}

Respond now as MindBridge. Return JSON only.`;

  const raw = await callModel(system, userPrompt);
  let parsed = parseModelOutput(raw);

  if (!parsed || !parsed.message) {
    const fallback = extractFallbackText(raw);
    if (!fallback) {
      throw new Error("[ConversationAgent] Model returned unparseable output and fallback extraction failed.");
    }
    parsed = { message: fallback, goalComplete: false, confidence: 0.3 };
  }

  let { message, goalComplete, confidence } = parsed;

  message = message.replace(/\{[\s\S]*?\}/g, "").replace(/\[GOAL_COMPLETE\]/g, "").trim();

  if (startsWithBannedOpener(message)) {
    const bannedPattern = BANNED_OPENERS.map((o) => escapeRegex(o)).join("|");
    const cleaned = message.replace(new RegExp(`^(${bannedPattern})[,.]?\\s*`, "i"), "");
    message = cleaned ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : message;
  }

  // Avoid starting two consecutive assistant replies with the same structure.
  const previousAi = lastAiMsgs[lastAiMsgs.length - 1] || "";
  if (previousAi && starterKey(previousAi) && starterKey(previousAi) === starterKey(message)) {
    const structureFixRaw = await callModel(
      "Rewrite the response with a clearly different opening structure while keeping meaning and warmth. Return only plain text.",
      `Previous assistant message: "${previousAi}"\nCurrent draft: "${message}"`
    );
    message = String(structureFixRaw || "").replace(/\{[\s\S]*?\}/g, "").trim() || message;
  }

  // Prevent multiple-choice emotional prompts; force open-ended style.
  if (hasMultipleChoiceQuestion(message)) {
    const openEndedRaw = await callModel(
      "Rewrite this into one empathic validation sentence followed by one open-ended question. Do not use multiple-choice options. Return plain text only.",
      `Draft: "${message}"\nPatient last message: "${lastUserMsg}"`
    );
    message = String(openEndedRaw || "").replace(/\{[\s\S]*?\}/g, "").trim() || message;
  }

  // Match user energy when they are brief/withdrawn.
  if (patientIsBrief && wordCount(message) > 42) {
    const shortenRaw = await callModel(
      "Shorten this to warm, natural, concise style for a withdrawn patient. Max 2 short sentences. Keep empathy and one focused question. Return plain text only.",
      `Draft: "${message}"\nPatient last message: "${lastUserMsg}"`
    );
    message = String(shortenRaw || "").replace(/\{[\s\S]*?\}/g, "").trim() || message;
  }

  // Recover from occasional model cut-offs that end mid-thought.
  if (hasDanglingEnding(message)) {
    const completeRaw = await callModel(
      "Rewrite this into a complete, natural reply with no cut-off ending. Keep the same clinical intent and warmth. 2-4 sentences max. Return plain text only.",
      `Draft reply: "${message}"\nPatient last message: "${lastUserMsg}"\nCurrent goal: "${currentGoal}"`
    );
    message = String(completeRaw || "").replace(/\{[\s\S]*?\}/g, "").trim() || message;
  }

  const isDuplicate = lastAiMsgs.slice(-3).some((past) => jaccard(past, message) >= 0.72);
  if (isDuplicate) {
    const rephraseRaw = await callModel(
      "You are a skilled psychiatric interviewer. Rephrase the following message so it sounds completely different while keeping the same meaning and warmth. Return only the rephrased message, no JSON, no explanation.",
      `Original: "${message}"\nConversation goal: "${currentGoal}"\nPatient's last message: "${lastUserMsg}"`
    );
    message = String(rephraseRaw || "").replace(/\{[\s\S]*?\}/g, "").trim() || message;
  }

  if (goalComplete && confidence < 0.75) {
    goalComplete = false;
  }

  if (!message) {
    throw new Error("[ConversationAgent] Post-processing produced empty message.");
  }

  return { text: message, goalComplete, confidence };
};

export const conversationAgent = async (arg1, arg2, arg3, arg4) => {
  const isObjectStyle = typeof arg1 === "object" && arg1 !== null && !Array.isArray(arg1);

  if (isObjectStyle) {
    const payload = {
      currentGoal: arg1.currentGoal,
      remainingGoals: arg1.remainingGoals || [],
      messages: arg1.messages || [],
      memoryChunks: arg1.memoryChunks || [],
      patientName: arg1.patientName || "Patient",
      callModel: arg1.callModel || callGroq,
    };
    return runConversationAgent(payload);
  }

  const legacyPayload = {
    currentGoal: arg1,
    remainingGoals: [],
    messages: arg2 || [],
    memoryChunks: arg3 || [],
    patientName: arg4 || "Patient",
    callModel: callGroq,
  };

  const result = await runConversationAgent(legacyPayload);
  return result.goalComplete ? `${result.text} [GOAL_COMPLETE]` : result.text;
};
